"use client"

import type React from "react"
import { useState, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { useUserSettingsStore } from "@/hooks/stores/schoolSettingsStore"
import { Upload, X, Check, User } from "lucide-react"

export function AccountSettingsTab() {
  const { settings, updateSettings } = useUserSettingsStore()
  const [fullName, setFullName] = useState(settings.fullName)
  const [email, setEmail] = useState(settings.email)
  const [username, setUsername] = useState(settings.username)
  const [phoneNumber, setPhoneNumber] = useState(settings.phoneNumber)
  const [bio, setBio] = useState(settings.bio)
  const [profileImage, setProfileImage] = useState(settings.profileImage || "/profile-avatar.png")
  const [isSaved, setIsSaved] = useState(false)
  
  // Create a ref for the file input
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file')
        return
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB')
        return
      }
      
      const reader = new FileReader()
      reader.onload = (event) => {
        if (event.target?.result) {
          setProfileImage(event.target.result as string)
        }
      }
      reader.onerror = () => {
        alert('Error reading file. Please try again.')
      }
      reader.readAsDataURL(file)
    }
  }

  const handleUploadClick = () => {
    // Trigger the hidden file input
    fileInputRef.current?.click()
  }

  const handleRemoveImage = () => {
    setProfileImage("/profile-avatar.png")
  }

  const handleSave = () => {
    updateSettings({
      fullName,
      email,
      username,
      phoneNumber,
      bio,
      profileImage,
    })
    setIsSaved(true)
    setTimeout(() => setIsSaved(false), 3000)
  }

  return (
    <div className="space-y-6 bg-white">
      {/* Profile Picture Section */}
      <Card className="p-6 border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4">Your Profile Picture</h3>
        <div className="flex items-center gap-6">
          <div className="relative">
            {profileImage && profileImage !== "/profile-avatar.png" ? (
              <img
                src={profileImage}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover border-2 border-border"
                onError={(e) => {
                  // Fallback if image fails to load
                  e.currentTarget.src = "/profile-avatar.png"
                }}
              />
            ) : (
              <div className="w-24 h-24 rounded-full border-2 border-border flex items-center justify-center bg-muted">
                <User className="w-12 h-12 text-muted-foreground" />
              </div>
            )}
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex gap-3">
              <Button 
                onClick={handleUploadClick} 
                className="bg-primary hover:bg-primary/90 text-white"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload New Picture
              </Button>
              <input 
                ref={fileInputRef}
                id="image-upload" 
                type="file" 
                accept="image/*" 
                onChange={handleImageUpload} 
                className="hidden" 
              />
              {profileImage && profileImage !== "/profile-avatar.png" && (
                <Button
                  variant="outline"
                  onClick={handleRemoveImage}
                  className="border-border text-foreground hover:bg-muted bg-transparent"
                >
                  <X className="w-4 h-4 mr-2" />
                  Remove Picture
                </Button>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Click "Upload New Picture" to select an image from your device. JPG, PNG, or GIF. Max 5MB.
            </p>
          </div>
        </div>
      </Card>

      {/* Form Fields */}
      <Card className="p-6 border border-border">
        <div className="space-y-6">
          {/* Name and Email Row */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-muted-foreground block mb-2">Full name</label>
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Michael Okafor"
                className="border-border bg-white text-foreground"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground block mb-2 flex items-center gap-2">
                Email address
                <span className="flex items-center gap-1 text-primary text-xs">
                  <Check className="w-4 h-4" />
                  Verified
                </span>
              </label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="christinebrown@gmail.com"
                className="border-border bg-white text-foreground"
              />
            </div>
          </div>

          {/* Username and Phone Row */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-muted-foreground block mb-2">Username</label>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="christinebrown"
                className="border-border bg-white text-foreground"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground block mb-2 flex items-center gap-2">
                Phone number
                <span className="flex items-center gap-1 text-primary text-xs">
                  <Check className="w-4 h-4" />
                  Verified
                </span>
              </label>
              <Input
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+1 945-913-2196"
                className="border-border bg-white text-foreground"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Save Button and Success Message */}
      <div className="flex items-center gap-4">
        <Button onClick={handleSave} className="bg-primary hover:bg-primary/90 text-white px-8">
          Update Profile
        </Button>
        {isSaved && (
          <div className="flex items-center gap-2 text-primary text-sm">
            <Check className="w-4 h-4" />
            <span>Changes saved successfully</span>
          </div>
        )}
      </div>
    </div>
  )
}